<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import {
  companyList,
  companyDel,
  companyCreate,
  companyUpload,
  companyModelCSV,
} from "@/common/api/rest/company";

import type {
  CompanyListType,
  CompanyPayloadType,
} from "@/common/types/company";
import type { FileType } from "@/common/types/common";

import { RegisterTheme } from "@/common/themes";
import { companyForm, fileUploadForm } from "@/components";
import {
  svgIcon,
  baseAvatar,
  baseTable,
  baseConfirm,
  baseModal,
} from "@/common/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "logo", label: null },
  { key: "name", label: t("common.labels.name") },
  { key: "document_name", label: t("common.labels.document") },
  { key: "document_number", label: t("common.labels.number") },
]);

const tableData = ref<CompanyListType[]>([]);

const selected = ref<CompanyListType | null>(null);
const payload = ref<CompanyPayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isEdit = ref(false);
const showModalAdd = ref(false);
const showModalUpload = ref(false);
const showConfirmDel = ref(false);

function fetchData() {
  if (!accountId.value) return;

  companyList(accountId.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModalAdd.value = true;
  payload.value = null;
}

function closeModal() {
  payload.value = null;
  showModalAdd.value = false;
  isEdit.value = false;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  companyCreate(accountId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        router.push(
          `/${accountId.value}/register/company/edit/${data.company_id}`,
        );
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: CompanyListType) {
  router.push(`/${accountId.value}/register/company/edit/${data.company_id}`);
}

function remove(data: CompanyListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);
  showConfirmDel.value = false;

  companyDel(accountId.value, selected.value.company_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function uploadCompany() {
  showModalUpload.value = true;
}

function uploadCompanyCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadCompanyConfirm() {
  if (!fileUpload.value) return;

  store.commit("SET_LOADING", true);

  companyUpload(accountId.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCompanyCancel();
        showModalUpload.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModelCompany() {
  companyModelCSV(accountId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watchEffect(() => {
  fetchData();
});
</script>

<template>
  <register-theme tab-active="company">
    <template #title>
      <svg-icon icon="company" size="2rem" />
      {{ t("common.labels.companies") }}
    </template>

    <template #content>
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        :upload="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:edit="edit"
        @btn:del="remove"
        @btn:upload="uploadCompany()"
      >
        <template #logo="{ data }">
          <base-avatar
            :url="data.avatar?.small"
            size="1.75rem"
            background="#ffffff"
          />
        </template>
      </baseTable>
    </template>
  </register-theme>

  <base-confirm
    :show="showConfirmDel"
    :title="
      t('common.labels.remove', {
        concat: t('common.labels.company'),
      }).toUpperCase()
    "
    :message="
      t('common.texts.remove_message', {
        concat: selected?.name,
      })
    "
    @cancel="removeCancel"
    @confirm="removeConfirm"
  />

  <base-modal
    :show="showModalAdd"
    :title="
      t('common.labels.add', {
        concat: t('common.labels.company'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.add')"
    :button-close="t('common.labels.cancel')"
    @close="closeModal()"
    @confirm="addConfirm()"
  >
    <template #content>
      <company-form :data="payload" @update-data="payload = $event" />
    </template>
  </base-modal>

  <base-modal
    :show="showModalUpload"
    :title="
      t('common.labels.upload', {
        concat: t('common.labels.company'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.send')"
    :button-close="t('common.labels.cancel')"
    @close="uploadCompanyCancel()"
    @confirm="uploadCompanyConfirm()"
  >
    <template #content>
      <file-upload-form
        :extensions="fileExtensions"
        :model="true"
        @update-data="fileUpload = $event"
        @model-download="downloadModelCompany()"
      />
    </template>
  </base-modal>
</template>
